import { useEffect, useState } from 'react'
import { useAttendance, AttendanceStatus } from '../../context/AttendanceContext'
import { 
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button
} from '@jira-clone/ui'
import { Clock, Coffee, Users, CheckCircle2, LogOut } from 'lucide-react'
import { formatDistanceToNowStrict } from 'date-fns'

const statusConfig: Record<string, { label: string, color: string, icon: any }> = {
  on_duty: { label: 'On Duty', color: 'bg-emerald-500', icon: CheckCircle2 },
  break: { label: 'On Break', color: 'bg-amber-500', icon: Coffee },
  meeting: { label: 'In Meeting', color: 'bg-blue-500', icon: Users },
}

export default function AttendanceDropdown() {
  const { status, checkInTime, changeStatus, checkOut } = useAttendance()
  const [elapsed, setElapsed] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!checkInTime || status === 'checked_out') {
      setElapsed('')
      return
    }

    const updateTimer = () => {
      // Using formatDistanceToNowStrict to get e.g. "9 hours 15 minutes"
      // date-fns doesn't have a direct "9h 15m" format out of the box, so we clean it up
      const dist = formatDistanceToNowStrict(new Date(checkInTime))
      // simple replacement to make it shorter
      setElapsed(dist.replace(' hours', 'h').replace(' hour', 'h').replace(' minutes', 'm').replace(' minute', 'm').replace(' seconds', 's').replace(' second', 's'))
    }

    updateTimer()
    const interval = setInterval(updateTimer, 60000)
    return () => clearInterval(interval)
  }, [checkInTime, status])

  if (status === 'not_checked_in' || status === 'checked_out') {
    return null
  }

  const currentConfig = statusConfig[status] || statusConfig.on_duty

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2 px-3 h-9 bg-card">
          <span className={`h-2 w-2 rounded-full ${currentConfig.color}`} />
          <span className="font-semibold text-sm hidden sm:inline-block">{currentConfig.label}</span>
          <span className="text-xs text-muted-foreground tabular-nums hidden sm:inline-block border-l border-border pl-2 ml-1">
            {elapsed}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="px-2 py-1.5 mb-1 border-b border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Change Status</p>
        </div>
        
        <div className="space-y-1">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-sm"
            onClick={() => { changeStatus('on_duty'); setIsOpen(false) }}
          >
            <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" />
            On Duty
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-sm"
            onClick={() => { changeStatus('break'); setIsOpen(false) }}
          >
            <Coffee className="mr-2 h-4 w-4 text-amber-500" />
            Break
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-sm"
            onClick={() => { changeStatus('meeting'); setIsOpen(false) }}
          >
            <Users className="mr-2 h-4 w-4 text-blue-500" />
            Meeting
          </Button>
        </div>

        <div className="mt-2 pt-2 border-t border-border">
          <Button 
            variant="destructive" 
            size="sm" 
            className="w-full text-sm"
            onClick={() => { checkOut(); setIsOpen(false) }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Check Out
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
